import React, { useEffect, useState } from 'react';
import {
    Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, IconButton, Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Select from 'react-select';
import { SupervisorService, TicketService } from '../../services';
import { TICKET_PRIORITY_OPTIONS } from '../../utils/constants';
import { dropDownStyles } from '../../utils/helpers';
import AppButton from '../CustomButton';

export type ConversionType = 'inquiry' | 'ticket' | 'salesQuery';

type Option = { value: number; label: string };
type PriorityOption = { value: string; label: string };

const TYPE_LABELS: Record<ConversionType, string> = {
    inquiry: 'General Enquiry',
    ticket: 'Ticket',
    salesQuery: 'Sales Enquiry',
};

// Render the menu in a portal so it isn't clipped by the dialog, and keep it
// above MUI's Dialog (zIndex 1300).
const selectStyles = {
    ...dropDownStyles,
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
};

interface ConversionModalProps {
    open: boolean;
    onClose: () => void;
    sourceType: ConversionType;
    targetType: ConversionType;
    sourceId: number | null;
    onSuccess?: () => void;
}

export default function ConversionModal({ open, onClose, sourceType, targetType, sourceId, onSuccess }: ConversionModalProps): React.JSX.Element {
    const isTicketTarget = targetType === 'ticket';
    const [priority, setPriority] = useState<PriorityOption | null>(TICKET_PRIORITY_OPTIONS[1]);
    const [categoryOptions, setCategoryOptions] = useState<Option[]>([]);
    const [subCategoryOptions, setSubCategoryOptions] = useState<Option[]>([]);
    const [subSubCategoryOptions, setSubSubCategoryOptions] = useState<Option[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<Option | null>(null);
    const [selectedSubCategory, setSelectedSubCategory] = useState<Option | null>(null);
    const [selectedSubSubCategory, setSelectedSubSubCategory] = useState<Option | null>(null);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open || !isTicketTarget) return;

        // Reset form each time the modal opens for a ticket conversion
        setPriority(TICKET_PRIORITY_OPTIONS[1]);
        setSelectedCategory(null);
        setSelectedSubCategory(null);
        setSubCategoryOptions([]);
        setSelectedSubSubCategory(null);
        setSubSubCategoryOptions([]);
        setError('');

        const loadCategories = async () => {
            try {
                const resp = await TicketService.getTicketCategoryDropdown();
                const rawData = resp?.data?.data ?? resp?.data ?? resp;
                const list = Array.isArray(rawData) ? rawData : [];
                setCategoryOptions(list.map((item: any) => ({
                    value: Number(item?.id),
                    label: String(item?.name),
                })));
            } catch (err) {
                console.error('Failed to load categories', err);
            }
        };
        loadCategories();
    }, [open, isTicketTarget]);

    const handleCategoryChange = async (option: Option | null) => {
        setSelectedCategory(option);
        setSelectedSubCategory(null);
        setSubCategoryOptions([]);
        setSelectedSubSubCategory(null);
        setSubSubCategoryOptions([]);
        if (!option) return;
        try {
            const resp = await TicketService.getTicketSubCategoryDropdown(option.value);
            const rawData = resp?.data?.data ?? resp?.data ?? resp;
            const list = Array.isArray(rawData) ? rawData : [];
            setSubCategoryOptions(list.map((item: any) => ({
                value: Number(item?.id),
                label: String(item?.name),
            })));
        } catch (err) {
            console.error('Failed to load sub-categories', err);
        }
    };

    const handleSubCategoryChange = async (option: Option | null) => {
        setSelectedSubCategory(option);
        setSelectedSubSubCategory(null);
        setSubSubCategoryOptions([]);
        if (!option || !selectedCategory) return;
        try {
            const resp = await TicketService.getTicketSubSubCategoryDropdown(selectedCategory.value, option.value);
            const rawData = resp?.data?.data ?? resp?.data ?? resp;
            const list = Array.isArray(rawData) ? rawData : [];
            setSubSubCategoryOptions(list.map((item: any) => ({
                value: Number(item?.id),
                label: String(item?.name),
            })));
        } catch (err) {
            console.error('Failed to load sub-sub-categories', err);
        }
    };

    const handleSubmit = async () => {
        if (!sourceId) return;

        if (isTicketTarget) {
            if (!priority) {
                setError('Please select a priority');
                return;
            }
            if (!selectedCategory) {
                setError('Please select a category');
                return;
            }
        }
        setError('');
        try {
            const payload: Record<string, any> = {
                sourceType,
                sourceId,
                targetType,
            };
            if (isTicketTarget) {
                payload.priority = priority?.value;
                payload.category = selectedCategory?.value;
                if (selectedSubCategory) payload.subCategory = selectedSubCategory.value;
                if (selectedSubSubCategory) payload.subSubCategory = selectedSubSubCategory.value;
            }
            await SupervisorService.moveConversion(payload);
            onSuccess?.();
            onClose();
        } catch (err: any) {
            console.error('Error adding ticket, error:', err);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pr: 6 }}>
                <Typography variant="h4">Move to {TYPE_LABELS[targetType]}</Typography>
                <IconButton
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 12, top: 12, color: (theme) => theme.palette.grey[500] }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                {isTicketTarget ? (
                    <Box>
                        <FormControl sx={{ mb: 2 }} fullWidth>
                            <Typography sx={{ fontSize: 16, color: '#000', mb: 0.5 }}>Priority*</Typography>
                            <Select
                                options={TICKET_PRIORITY_OPTIONS}
                                value={priority}
                                onChange={(option) => setPriority(option as PriorityOption | null)}
                                placeholder="Select Priority"
                                menuPortalTarget={document.body}
                                styles={selectStyles}
                            />
                        </FormControl>
                        <FormControl sx={{ mb: 2 }} fullWidth>
                            <Typography sx={{ fontSize: 16, color: '#000', mb: 0.5 }}>Category*</Typography>
                            <Select
                                options={categoryOptions}
                                value={selectedCategory}
                                onChange={(option) => handleCategoryChange(option as Option | null)}
                                isClearable
                                placeholder="Select Category"
                                menuPortalTarget={document.body}
                                styles={selectStyles}
                            />
                        </FormControl>
                        <FormControl sx={{ mb: 1 }} fullWidth>
                            <Typography sx={{ fontSize: 16, color: '#000', mb: 0.5 }}>Sub Category</Typography>
                            <Select
                                options={subCategoryOptions}
                                value={selectedSubCategory}
                                onChange={(option) => handleSubCategoryChange(option as Option | null)}
                                isClearable
                                isDisabled={!selectedCategory}
                                placeholder={selectedCategory ? 'Select Sub Category' : 'Select Category first'}
                                menuPortalTarget={document.body}
                                styles={selectStyles}
                            />
                        </FormControl>
                        <FormControl sx={{ mb: 1 }} fullWidth>
                            <Typography sx={{ fontSize: 16, color: '#000', mb: 0.5 }}>Sub-Sub Category</Typography>
                            <Select
                                options={subSubCategoryOptions}
                                value={selectedSubSubCategory}
                                onChange={(option) => setSelectedSubSubCategory(option as Option | null)}
                                isClearable
                                isDisabled={!selectedSubCategory}
                                placeholder={selectedSubCategory ? 'Select Sub-Sub Category' : 'Select Sub Category first'}
                                menuPortalTarget={document.body}
                                styles={selectStyles}
                            />
                        </FormControl>
                        {error && <FormHelperText error>{error}</FormHelperText>}
                    </Box>
                ) : (
                    <Typography variant="body1">
                        Are you sure you want to move this {TYPE_LABELS[sourceType]} to {TYPE_LABELS[targetType]}?
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                <AppButton
                    variant="contained"
                    className="sm"
                    sx={{ mr: 1.5 }}
                    onClick={handleSubmit}
                >
                    Move
                </AppButton>
                <AppButton variant="outlined" className="sm" onClick={onClose}>
                    Cancel
                </AppButton>
            </DialogActions>
        </Dialog>
    );
}
